import { HostListener, Component, ElementRef, Inject, NgZone, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { StaticEnum } from '../../../objects/advance-searchbox';
import { ListingService } from '../../../services/listing.service';
import { AsConfigService } from '../../../shared/external-libraries/ngx-advanced-searchbox/src/public_api';
import { SearchService } from '../../../services/search.service';
import { BehaviorSubject, Observable, Subject, combineLatest } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, pairwise, scan, shareReplay, startWith, switchMap, takeUntil, tap } from 'rxjs/operators';
import { MatStepper } from '@angular/material/stepper';
import { NotificationService } from '../../../services/notification.service';
import { LooseObject } from '../../../objects/loose-object';
import { AdvanceReportService } from '../../../services/advance-report.service';
import { HttpErrorResponse } from '@angular/common/http';
import { clone, cloneDeep, isEmpty, pick, reduce } from 'lodash-es';
import { TranslateService } from '@ngx-translate/core';
import { AdvanceReportTemplate, ChartConfig } from '../../../objects/advance-reports';
import { fallback, data_get, filled, blank, data_is_true, Identifiable } from '../../../shared/utils/common';
import { ClientStoreService } from '../../../services/client-store.service';

import Sortable, { MultiDrag } from 'sortablejs';
import { FieldmagicDataGridColumnDef, FieldmagicDataGridColumnType, FieldmagicDataGridOnReadyEvent, FieldmagicDataGridOptions } from '../../../shared/components/data-grid/data-grid.component';
import { DotNotationPipe } from '../../../pipes/dot-notation/dot-notation.pipe';
import { NormalizeValuePipe } from '../../../pipes/normalize-value/normalize-value.pipe';
Sortable.mount(new MultiDrag());

@Component({
  selector: 'app-advance-reports-wizard',
  templateUrl: './wizard.component.html',
  styleUrls: ['./wizard.component.scss']
})
export class WizardComponent implements OnInit {
  @ViewChild('availableFieldsElem') availableFieldsElem!: ElementRef;
  @ViewChild('usedFieldsElem') usedFieldsElem!: ElementRef;
  @ViewChild('datapointsListElem') datapointsListElem!: ElementRef;

  isMobile: boolean;

  availableFieldsSortable: Sortable;
  usedFieldsSortable: Sortable;
  datapointsListElemSortable: Sortable;

  unsubscribeSubscriptions: Subject<void> = new Subject<void>();

  moduleList$: Observable<AdvanceReportTemplate>;
  assetTypeList$: Observable<object[]>;

  moduleFieldList: AdvanceReportTemplate[] = [];
  fieldList: AdvanceReportTemplate[] = [];
  filterList: AdvanceReportTemplate[] = [];
  fieldDetails: LooseObject;
  availableFieldList: AdvanceReportTemplate[] = [];

  chartTypes = ['bar', 'line'];
  groupingTypes = ['date_histogram', 'terms', 'histogram'];

  // For date_histogram
  dateTimeFields: AdvanceReportTemplate[] = [];
  calendarInterval = ['week', 'month', 'quarter', 'year'];

  // For histogram
  numericFields: AdvanceReportTemplate[] = [];
  minGranularity = 1;
  maxGranularity = 1000;

  // For Top Values
  dropdownFields: AdvanceReportTemplate[] = [];
  minTopValueSize = 1;
  maxTopValueSize = 50;

  // For datasets
  stackableFields: AdvanceReportTemplate[] = [];
  aggregationType = ['sum', 'min', 'max', 'value_count', 'field'];
  aggregationTypeDatapoint = ['sum', 'min', 'max', 'value_count'];

  generalSetupForm: FormGroup;
  chartSetupForm: FormGroup;
  datapointSetupForm: FormGroup;
  fieldAliases = new FormControl({});

  // Setup Preview
  dashboardData$: Observable<LooseObject[]>;
  resultSet: LooseObject[];
  resultDatapoints: LooseObject[];

  multiselectMessages = {
    'minlength': (params: { requiredLength: number }) => this.translateService.instant('validation_error_messages.min_select', { min: params.requiredLength }),
    'maxlength': (params: { requiredLength: number }) => this.translateService.instant('validation_error_messages.max_select', { max: params.requiredLength })
  };

  departmentRestriction = false;

  searchAvailableFields = new FormControl('');

  options: FieldmagicDataGridOptions = {
    enable_ui_grouping: false,
    disable_aggregates_on_header: true,
    enable_row_selection: 'single',
    enable_charts: false,
    enable_range_selection: false,
    enable_totals: false,
  };
  columns: FieldmagicDataGridColumnDef[] = [];

  private searchTerm$ = new BehaviorSubject<string>('');
  private page$ = new BehaviorSubject<number>(1);
  private hasMorePages = true;

  @HostListener('window:resize', ['$event'])
  onResize(event): void {
    this.checkScreenSize();
  }

  constructor(
    public dialogRef: MatDialogRef<WizardComponent>,
    @Inject(MAT_DIALOG_DATA) public dialogData: any,
    public formBuilder: FormBuilder,
    public listService: ListingService,
    public asConfig: AsConfigService,
    public searchService: SearchService,
    public advanceReportService: AdvanceReportService,
    public notifService: NotificationService,
    public translateService: TranslateService,
    public ngZone: NgZone,
    private client: ClientStoreService,
    private dotNotation: DotNotationPipe,
    private normalizeValue: NormalizeValuePipe,
  ) {
    this.departmentRestriction = this.client.isDepartmentTracking() && this.client.isDepartmentRestricted();
  }

  ngOnInit() {
    this.generalSetupForm = this.formBuilder.group({
      name: ['', [Validators.required, Validators.minLength(5), Validators.maxLength(128)]],
      module: ['', Validators.required],
      asset_type_id: [null],
      fields: [[], [Validators.required, Validators.minLength(1)]],
      filters: [{}],
      show_in_dashboard: [false]
    });
    this.chartSetupForm = this.formBuilder.group({
      chart_configs: this.formBuilder.array([], [Validators.min(1), Validators.max(4)])
    });
    this.datapointSetupForm = this.formBuilder.group({
      datapoints: this.formBuilder.array([], [Validators.min(1), Validators.max(6)])
    });

    this.listService.setModule('advance_reports');
    this.moduleList$ = this.listService.getAdvancedSearchboxTemplate().pipe(
      takeUntil(this.unsubscribeSubscriptions),
      map((response) => response.find(x => x.model === 'module')),
      map((response) => {
        return {
          ...response,
          domains: (response.domains as StaticEnum[]).map(x => {
            return {
              ...x,
              label: this.translateService.instant(x.label),
            }
          })
        }
      })
    );

    this.assetTypeList$ = this.searchTerm$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap((term) =>
        this.page$.pipe(
          switchMap((page) => {
            return this.advanceReportService.searchAssetTypes(term, page).pipe(
              map((response) => {
                return response.map(x => {
                  return {
                    label: x.name,
                    value: x.id,
                    attributes: x.attributes,
                  }
                })
              }),
              tap((response) => this.hasMorePages = response.length > 0)
            )
          }),
          scan((acc, items) => (this.page$.value === 1 ? items : [...acc, ...items]), []),
        )
      )
    );

    this.generalSetupForm.get('module').valueChanges
      .pipe(startWith(''), pairwise(), takeUntil(this.unsubscribeSubscriptions))
      .subscribe(([prevValue, newValue]) => {
        if (prevValue) {
          while (this.chartConfigForms.length > 0) {
            this.chartConfigForms.removeAt(0);
          }
          while (this.datapoints.length > 0) {
            this.datapoints.removeAt(0);
          }
          this.generalSetupForm.get('fields').reset([]);
          this.generalSetupForm.get('filters').reset();
        }
        this.generalSetupForm.get('asset_type_id').reset();

        this.listService.setModule(newValue);
        const metadata$ = this.advanceReportService.getModuleMetadata(newValue);
        metadata$.pipe(takeUntil(this.unsubscribeSubscriptions)).subscribe((template) => {
          this.setupAdvanceSearchbox(template);
          this.moduleFieldList = clone(this.fieldList);
          this.availableFieldList = this.fieldList;
          this.searchAvailableFields.setValue('');
        });
      });

    this.generalSetupForm.get('fields').valueChanges
      .pipe(startWith([]), pairwise(), takeUntil(this.unsubscribeSubscriptions))
      .subscribe(([prevValue, newValue]) => {
        newValue = newValue || [];
        this.availableFieldList = this.fieldList.filter(x => newValue.includes(x.model) === false);

        const current = this.searchAvailableFields.value;
        this.searchAvailableFields.setValue('', { emitEvent: true });
        this.searchAvailableFields.setValue(current, { emitEvent: true });
      });

    if (this.dialogData.isNew === false) {
      this.generalSetupForm.patchValue(pick(this.dialogData.recordDetails, ['name', 'module', 'asset_type_id', 'show_in_dashboard']));
      this.fieldAliases.setValue(this.dialogData.recordDetails.field_aliases);
      if (filled(this.dialogData.recordDetails.asset_type_id)) {
        this.searchTerm$.next(this.dialogData.recordDetails.asset_type_name);
        this.assetTypeList$.subscribe((assetTypeList) => {
          const selectedOption = assetTypeList.find(x => x['value'] === this.dialogData.recordDetails.asset_type_id);
          this.loadAssetTypeFields(selectedOption, false);
          this.generalSetupForm.get('fields').setValue(this.dialogData.recordDetails.fields);
        });
      } else {
        this.generalSetupForm.get('fields').setValue(this.dialogData.recordDetails.fields);
      }

      // PHP return this value as [] if not defined. And when object is instatiated using array
      // It will return an empty json when stringify it
      this.generalSetupForm.get('filters').setValue(
        isEmpty(this.dialogData.recordDetails.filters) ? {} : cloneDeep(this.dialogData.recordDetails.filters)
      );

      let chartConfigs = this.dialogData.recordDetails.chart_config as LooseObject[];
      chartConfigs.forEach((config, index) => {
        this.addChart(config.datasets.length);

        this.chartConfigForms.controls[index].patchValue({
          "date_histogram": { "field": null, "calendar_interval": null },
          "histogram": { field: null, interval: null },
          "terms": { field: null, size: null },
          ...config
        });
      });

      const arDatapoints: LooseObject[] = fallback(data_get(this.dialogData, 'recordDetails.datapoints'), {
        fallback: () => [],
      });

      arDatapoints.forEach((datapoint, index) => this.addDatapoint(datapoint));
    }

    this.searchAvailableFields.valueChanges
      .pipe(distinctUntilChanged())
      .subscribe(searchValue => {
        this.availableFieldList = this.fieldList.filter(f => {
          const IsPresentOnAvailableList = (f.translated_label.toLowerCase()).includes(searchValue.toLowerCase());
          const isFieldUsed = this.selectedFields.some(sf => sf === f.model);
          return IsPresentOnAvailableList && !isFieldUsed;
        });
      });
  }

  ngAfterViewInit() {
    this.initSortables();
  }

  checkScreenSize(): void {
    /// 992 is lg size in bootstrap
    this.isMobile = window.innerWidth < 992;
  }

  loadAssetTypeFields(selectedOption, resetFields: boolean = true) {
    if (filled(selectedOption)) {
      this.fieldList = this.moduleFieldList.concat(selectedOption.attributes.map(x => {
        return {
          ...x,
          translated_label: x.label,
        }
      }));
      this.fieldDetails = reduce(this.fieldList, (obj, param) => {
        obj[param.model] = {
          type: param.dataType,
          label: param.label,
        };
        return obj;
      }, {});

      /// rebuild the dropdown fields based on selected asset type
      this.dropdownFields = this.fieldList.filter(x => FIELDS_AVAILABLE_FOR_GROUPING.includes(x.dataType));
    }

    if (resetFields) {
      this.generalSetupForm.get('fields').reset([]);
    }
  }

  get singleChartForm(): FormGroup {
    const form = this.formBuilder.group({
      name: [null, [Validators.required, Validators.minLength(5), Validators.maxLength(128)]],
      grouping_type: [null, Validators.required],
      date_histogram: this.formBuilder.group({
        field: [null, Validators.required],
        calendar_interval: ['month', Validators.required],
      }),
      histogram: this.formBuilder.group({
        field: [null, Validators.required],
        interval: [null, [Validators.required, Validators.min(1), Validators.max(100000000)]],
      }),
      terms: this.formBuilder.group({
        field: [null, Validators.required],
        size: [5, [Validators.required, Validators.min(1), Validators.max(10)]],
      }),
      datasets: this.formBuilder.array([], [Validators.min(1), Validators.max(2)]),
    });

    form.get('date_histogram').disable();
    form.get('histogram').disable();
    form.get('terms').disable();

    return form;
  }

  get singleDatasetForm(): FormGroup {
    const form = this.formBuilder.group({
      name: [this.translateService.instant('advance_report_lang.record_count'), [Validators.required, Validators.minLength(5), Validators.maxLength(128)]],
      chart_type: ['bar', Validators.required],
      type: ['value_count', Validators.required],
      field: [null, Validators.required],
      color: ['#ff6347', Validators.required],
    });
    form.get('type').valueChanges.pipe(takeUntil(this.unsubscribeSubscriptions)).subscribe((value) => {
      form.get('field').enable({emitEvent: false});
      if (value === 'value_count') {
        form.get('field').disable();
        form.get('field').setValue(null);
        form.get('name').setValue(this.translateService.instant('advance_report_lang.record_count'));
      }
    });
    form.get('field').valueChanges.pipe(takeUntil(this.unsubscribeSubscriptions)).subscribe((aggField) => {
      const aggType = form.get('type').value;
      if (filled(this.fieldList) && filled(aggType) && filled(aggField)) {
        const fieldDetail = this.fieldList.find(field => field.model === aggField);
        const additionalLabel = aggType !== 'field' ? ` (${this.translateService.instant('advance_report_lang.aggregation_types.' + aggType)})` : '';
        form.get('name').setValue(`${fieldDetail.translated_label}${additionalLabel}`);
      }
    });
    form.get('field').disable();
    return form;
  }

  get chartConfigForms(): FormArray {
    return this.chartSetupForm.get('chart_configs') as FormArray;
  }

  datasetForm(chartIndex: number): FormArray {
    return this.chartConfigForms.controls[chartIndex].get('datasets') as FormArray;
  }

  removeChart(index: number) {
    this.chartConfigForms.removeAt(index);
  }

  addChart(datasetCount: number = 1) {
    if (this.chartConfigForms.length >= 4) {
      this.notifService.notifyError('advance_report_lang.max_charts');
      return;
    }
    const singleChartForm = cloneDeep(this.singleChartForm);

    singleChartForm.get('grouping_type').valueChanges.pipe(takeUntil(this.unsubscribeSubscriptions)).subscribe((value) => {
      this.groupingTypes.forEach((type) => {
        singleChartForm.get(type).disable();
      });
      singleChartForm.get(value).enable();
    });

    for (let x = 0; x < datasetCount; x++) {
      (singleChartForm.get('datasets') as FormArray).push(this.singleDatasetForm);
    }

    combineLatest([
      singleChartForm.get('date_histogram.field').valueChanges,
      singleChartForm.get('histogram.field').valueChanges,
      singleChartForm.get('terms.field').valueChanges,
    ]).pipe(
      map(([dateModel, histModel, termsModel]) => {
        return dateModel || histModel || termsModel;
      }),
      takeUntil(this.unsubscribeSubscriptions)
    ).subscribe(groupByField => {
      if (filled(this.fieldList) && filled(groupByField)) {
        const fieldDetail = this.fieldList.find(field => field.model === groupByField);
        singleChartForm.get('name').setValue(fieldDetail.translated_label);
      }
    });

    this.chartConfigForms.push(singleChartForm);
  }

  removeDataset(chartIndex: number, datasetIndex: number) {
    if (this.datasetForm(chartIndex).length === 1) {
      this.notifService.notifyError('advance_report_lang.min_datasets');
      return;
    }
    this.datasetForm(chartIndex).removeAt(datasetIndex);
  }

  addDataset(chartIndex: number) {
    // Default color for every new dataset
    const colorIndex = [
      '#ff6347', // Tomato Red,
      '#4caf50', // Leaf Green,
      '#2196f3', // Sky Blue,
      '#ff69b4', // Hot Pink,
      '#ffd700', // Gold,
      '#00ced1', // Dark Turquoise,
      '#ff4500', // Orange Red,
      '#8a2be2', // Blue Violet,
    ];

    if (this.datasetForm(chartIndex).length >= 2) {
      this.notifService.notifyError('advance_report_lang.max_datasets');
      return;
    }
    const singleDatasetForm = this.singleDatasetForm;
    singleDatasetForm.patchValue({
      color: colorIndex[this.datasetForm(chartIndex).length]
    });
    this.datasetForm(chartIndex).push(singleDatasetForm);
  }

  getAggregationTypes(aggegationTypes: string[]) {
    if (blank(this.numericFields)) {
      return aggegationTypes.filter(x => ['sum', 'min', 'max'].includes(x) === false);
    }
    return aggegationTypes;
  }

  setupAdvanceSearchbox(template: AdvanceReportTemplate[]): void {
    // Setup Template
    template = template.map(x => {
      const extraLabel = filled(x.extraLabel) ? ` (${this.translateService.instant(x.extraLabel)})` : '';
      const relatedTable = filled(x.relatedTable) ? `${this.translateService.instant(x.relatedTable)} - ` : '';
      return {
        ...x,
        translated_label: relatedTable + this.translateService.instant(x.label) + extraLabel,
      }
    });
    this.fieldDetails = reduce(template, (obj, param) => {
      obj[param.model] = {
        type: param.dataType,
        label: param.label,
        related_table: param.relatedTable,
        extraLabel: param.extraLabel || null,
      };
      return obj;
    }, {});
    this.fieldList = template.filter(x => data_is_true(x.asField));
    this.filterList = template.filter(x => data_is_true(x.asFilter));
    this.dateTimeFields = this.fieldList.filter(x => x.dataType === 'datetime');
    this.numericFields = this.fieldList.filter(x => ['currency', 'number'].includes(x.dataType));
    this.dropdownFields = this.fieldList.filter(x => FIELDS_AVAILABLE_FOR_GROUPING.includes(x.dataType));
    this.stackableFields = this.fieldList.filter(x => FIELDS_AVAILABLE_FOR_STACKING.includes(x.dataType));

    // Get filter with remote related data
    this.filterList.forEach((asTemplate: AdvanceReportTemplate) => {
      if (asTemplate.domains && asTemplate.domains === 'remote') {
        this.asConfig.customDomainsAsyncFn[asTemplate.model] = (observable, viewModel, model) => {
          return observable.pipe(
            debounceTime(400),
            switchMap((term) => {
              return this.searchService.getRemoteData(asTemplate, term);
            }),
          );
        };
      }
    });
  }

  /**
   * When the user clicks next.
   *
   * @param {MatStepper} stepper
   */
  next(stepper: MatStepper, form: FormGroup): void {
    this.touchAllFields(form);
    if (form.invalid) {
      this.notifService.notifyError('please_complete_the_form');
    } else {
      stepper.next();
      if (stepper.selectedIndex === 3) {
        this.generateDashboard();
      }
    }
  }

  touchAllFields(form: FormGroup) {
    for (let fieldName in form.controls) {
      if (form.controls[fieldName] instanceof FormArray) {
        let formGroups = form.controls[fieldName] as FormArray;
        formGroups.controls.forEach((formGroup: FormGroup) => {
          this.touchAllFields(formGroup);
        });
      } else if (form.controls[fieldName] instanceof FormGroup) {
        this.touchAllFields(form.controls[fieldName] as FormGroup);
      } else {
        form.controls[fieldName].markAsTouched();
        form.controls[fieldName].markAsDirty();
      }
    }
  }

  closeDialog(action: string, data: LooseObject = {}) {
    this.unsubscribeSubscriptions.next();
    this.unsubscribeSubscriptions.complete();
    this.dialogRef.close({
      action: action,
      data: data
    });
  }

  generateDashboard() {
    this.dashboardData$ = this.advanceReportService.getAdvanceReportData({
      filters: this.generalSetupForm.get('filters').value,
      fields: this.generalSetupForm.get('fields').value,
      datapoints: this.datapointSetupForm.get('datapoints').value,
      chart_config: this.chartConfigForms.value as ChartConfig,
      asset_type_id: filled(this.generalSetupForm.get('asset_type_id').value) ? this.generalSetupForm.get('asset_type_id').value : null,
    }, this.generalSetupForm.get('module').value).pipe(
      shareReplay(),
      tap((response) => {
        const tableColumns = this.generalSetupForm.get('fields').value;
        this.resultSet = response.resultset.map(row => {
          let updatedRow = {};
          tableColumns.forEach((field) => {
            let value = this.dotNotation.transform(row, field);
            value = this.normalizeValue.transform(value, this.fieldDetails[field].type);
            updatedRow[field] = value;
          });
          return updatedRow;
        });
        this.resultDatapoints = response.datapoints;
        this.initColumns(this.generalSetupForm.get('fields').value);
      }),
      map((response) => {
        return filled(response.aggregations) ? Object.keys(response.aggregations).map(chartId => {
          return this.advanceReportService.aggregationResultToChart(response.aggregations[chartId], response.config[chartId]);
        }) : [];
      })
    );
  }

  saveDashboard() {
    this.advanceReportService.saveAdvanceReportData({
      name: this.generalSetupForm.get('name').value,
      filters: this.generalSetupForm.get('filters').value,
      fields: this.generalSetupForm.get('fields').value,
      field_aliases: this.fieldAliases.value,
      datapoints: this.datapointSetupForm.get('datapoints').value,
      chart_config: this.chartConfigForms.value,
      asset_type_id: filled(this.generalSetupForm.get('asset_type_id').value) ? this.generalSetupForm.get('asset_type_id').value : null,
      show_in_dashboard: this.generalSetupForm.get('show_in_dashboard').value,
    }, this.generalSetupForm.get('module').value, this.dialogData.id || null).pipe(takeUntil(this.unsubscribeSubscriptions)).subscribe((response) => {
      this.closeDialog('save', response);
    }, (error: HttpErrorResponse) => {
      if (error.status == 400 || error.status == 422) {
        this.notifService.notifyWarning('record_invalid_parameters');
      }
    });
  }

  // Asset type infinite scroll functions

  onSearch(event): void {
    this.searchTerm$.next(event.term);
    this.page$.next(1);
  }

  onScrollToEnd(): void {
    if (this.hasMorePages) {
      this.page$.next(this.page$.value + 1);
    }
  }

  // Draggable field related functions

  initSortables(): void {
    if (this.availableFieldsSortable) this.availableFieldsSortable.destroy();
    if (this.usedFieldsSortable) this.usedFieldsSortable.destroy();
    if (this.datapointsListElemSortable) this.datapointsListElemSortable.destroy();
    this.ngZone.runOutsideAngular(() => {
      this.availableFieldsSortable = Sortable.create(this.availableFieldsElem.nativeElement, {
        group: {
          name: 'sortable-selected-field-list',
          // pull: 'clone',
          pull: true,
          put: false,
        },
        filter: '.list-group-item-dark',
        multiDrag: true,
        selectedClass: "active",
        animation: 150,
        sort: false
      });
      this.usedFieldsSortable = Sortable.create(this.usedFieldsElem.nativeElement, {
        group: {
          name: 'sortable-selected-field-list',
          pull: true,
          put: true,
        },
        multiDrag: true,
        scroll: true,
        scrollSensitivity: 200,
        selectedClass: "active",
        animation: 150,
        // handle: '.drag-handle',
        filter: 'input',
        preventOnFilter: false,
        // When cloning field from available fields
        onAdd: (event: Sortable.SortableEvent) => {
          this.ngZone.runTask(() => {
            const currentFields = this.generalSetupForm.get('fields').value;
            const draggedEls = (event['items'].length > 0 ? event['items'] : [event.item]) as HTMLElement[];
            const models = draggedEls.map(el => el.getAttribute('data-model'));
            draggedEls.forEach(cloneEl => {
              cloneEl.parentNode.removeChild(cloneEl);
            });
            currentFields.splice(event.newIndex, 0, ...models);
            this.generalSetupForm.get('fields').setValue(currentFields);
          });
        },
        // When reordering fields
        onUpdate: (event: Sortable.SortableEvent) => {
          this.ngZone.run(() => {
            const currentFields = this.generalSetupForm.get('fields').value;
            const draggedEls = (event['items'].length > 0 ? event['items'] : [event.item]) as HTMLElement[];
            const models = draggedEls.map(el => el.getAttribute('data-model'));
            let newFields = currentFields.filter(cf => models.includes(cf) == false);
            newFields.splice(event.newIndex, 0, ...models);
            this.generalSetupForm.get('fields').setValue(newFields);
          });
        },
      });
    });
  }

  get selectedFields(): string[] {
    return this.generalSetupForm.get('fields').value
  }

  getFieldTemplate(model) {
    return this.fieldList.find(fl => fl.model === model);
  }

  trackFieldList(index: number, item: AdvanceReportTemplate) {
    return item.model;
  }

  isFieldUsed(model: string) {
    return this.selectedFields.some(sf => sf === model);
  }

  removeField(event: Event, model: string = null) {
    event.stopPropagation();
    const newValue = { ...this.fieldAliases.value };
    if (model) {
      this.generalSetupForm.get('fields').setValue(this.generalSetupForm.get('fields').value.filter(x => x !== model));
      delete newValue[model];
    } else {
      this.selectedFields.forEach(sf => {
        delete newValue[sf];
      });
      this.generalSetupForm.get('fields').setValue([]);
    }
    this.fieldAliases.setValue(newValue);
  }

  syncFieldAliases(key: string, value: any) {
    this.fieldAliases.setValue({
      ...this.fieldAliases.value,
      [key]: value
    });
  }

  // Datapoint related functions

  get datapoints(): FormArray {
    return this.datapointSetupForm.get('datapoints') as FormArray;
  }

  get datapoint(): FormGroup {
    const form = this.formBuilder.group({
      name: [this.translateService.instant('advance_report_lang.record_count'), [Validators.required, Validators.minLength(5), Validators.maxLength(128)]],
      filters: [{}],
      type: ['value_count', Validators.required],
      field: [null, Validators.required],
    });
    form.get('type').valueChanges.pipe(takeUntil(this.unsubscribeSubscriptions)).subscribe((value) => {
      form.get('field').enable({emitEvent: false});
      if (value === 'value_count') {
        form.get('field').disable();
        form.get('field').setValue(null);
        form.get('name').setValue(this.translateService.instant('advance_report_lang.record_count'));
      }
    });
    form.get('field').valueChanges.pipe(takeUntil(this.unsubscribeSubscriptions)).subscribe((aggField) => {
      const aggType = form.get('type').value;
      if (filled(this.fieldList) && filled(aggType) && filled(aggField)) {
        const fieldDetail = this.fieldList.find(field => field.model === aggField);
        const additionalLabel = aggType !== 'field' ? ` (${this.translateService.instant('advance_report_lang.aggregation_types.' + aggType)})` : '';
        form.get('name').setValue(`${fieldDetail.translated_label}${additionalLabel}`);
      }
    });
    form.get('field').disable();
    return form;
  }

  addDatapoint(initialValue: LooseObject = null) {
    if (this.datapoints.length >= 6) {
      this.notifService.notifyError('advance_report_lang.max_datapoint');
      return;
    }

    const datapoint = this.datapoint;

    if (initialValue) {
      datapoint.patchValue(initialValue);
    }

    this.datapoints.push(datapoint);
  }

  removeDatapoint(index: number) {
    this.datapoints.removeAt(index);
  }

  initColumns(fields: string[]) {
    const fieldAliases = this.fieldAliases.value;
    this.columns = fields.map((field): FieldmagicDataGridColumnDef => {
      const fieldDetail = this.fieldDetails[field];
      let fieldLabel = '--';
      if (fieldDetail) {
        if (fieldAliases[field]) {
          fieldLabel = fieldAliases[field];
        } else {
          fieldLabel = filled(fieldDetail['related_table']) ? this.translateService.instant(fieldDetail['related_table']) + ' - ' : '';
          fieldLabel += `${this.translateService.instant(fieldDetail.label)}`;
          fieldLabel += filled(fieldDetail['extraLabel']) ? `(${this.translateService.instant(fieldDetail.extraLabel)})` : '';
        }
        let fieldType: FieldmagicDataGridColumnType = 'text';
        switch (fieldDetail.type) {
          case 'uuid':
            fieldType = 'text';
            break;
          case 'number':
          case 'decimal':
            fieldType = 'numeric';
            break;
          case 'date':
          case 'datetime':
            fieldType = 'datetime';
          default:
            fieldType = fieldDetail.type;
            break;
        }
        return {
          label: fieldLabel,
          field_name: field,
          field_type: fieldType,
          sortable: false,
          editable: false,
        };
      } else {
        return {
          label: '--',
          field_name: field,
          field_type: 'text',
          sortable: false,
          editable: false,
        };
      }
    });
  }

  initDataGrid(event: FieldmagicDataGridOnReadyEvent<Identifiable>) {
    if(this.columns.length <= 8) {
      event.api.fitColumnsToTable();
    }
  }
}

const FIELDS_AVAILABLE_FOR_GROUPING = [
  'dropdown',
  'multiselect',
  'uuid',
  'checkbox',
  'text',
  'number',
  // FC-4729: For phone number
  'textarea'
];

const FIELDS_AVAILABLE_FOR_STACKING = [
  'dropdown',
  'multiselect',
  'text',
  'number',
  'textarea'
];